/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { Layout, notification } from "antd";
import { Redirect, useHistory } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import dayjs from 'dayjs'

// import CSS
import "./HomePage.css";

// import functions
import { SocketContextProvider } from "../../context/socketContext";
import { TablePreferencesProvider } from '../../context/TablePreferencesProvider'
import messageStore from "../../redux/message";
import notifications from "../../redux/notifications";
import physicianDetails from "../../redux/physicianDetails";
import entities from "../../redux/entities";
import additionalInfoRequired from "../../redux/additionalInfoRequired";
import rxChange from "../../redux/rxChange";
import rxRenewal from "../../redux/rxRenewals";
import caseTickets from "../../redux/caseTickets";
import referrals from "../../redux/referrals"
import caseQueue from "../../redux/caseQueue"
import oneClickPrescribeQueue from "../../redux/oneClickPrescribeQueue"
import rxRequests from "../../redux/rxRequests"
import configs from "../../redux/configs"
import { useReportsWithSockets } from "../../hooks/useReportsWithSockets";

import socket, { socket_init, socket_close } from "../../axios/socket";

// import components
import HeaderPage from "./components/HeaderPage";
import { Sidebar } from "./components/Sidebar";
import { VerifyIdentity } from "./IdentityVerification";

import { ConsultTypeEnum, PhysicianEmploymentStatusEnum, PhysicianTagEnum } from "../../types/enums"
import { AppRouter } from './components/AppRouter'
import config from '../../config'

import { useToggleMenu } from "../../context/MenuProvider";
import { UserRoleEnum } from '../../types/enums'
import { useAuthentication, useAuthenticatedSession } from '../../context/AuthProvider';

const {
  STORE_AND_FORWARD,
  AUDIO
} = ConsultTypeEnum

const { Content } = Layout;

const HomePage = (props) => {
  const { actions, loggedInPhysicianDetails } = props;
  const [showIdentityVerificationFlow, setShowIdentityVerificationFlow] = useState(false)
  const { isMenuOpen } = useToggleMenu();
  const { accessToken } = useAuthentication();
  const { user } = useAuthenticatedSession();

  const history = useHistory();
  const { caseCapMet } = useSelector(state => state.caseQueue)
  const isInternalPhysician = loggedInPhysicianDetails?.employmentStatus === 'internal physicians'
  const PROVIDER_HANDLES_TESTO_CASES = loggedInPhysicianDetails?.teleMedicineCategories?.includes('Testosterone')

  const physicianTags = loggedInPhysicianDetails?.employmentStatus.tags || []

  const physicianUsesReferralsFeature =
    physicianTags.includes(PhysicianTagEnum.NEEDS_REVIEW) ||
    physicianTags.includes(PhysicianTagEnum.REVIEWER)

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const screenWidthRef = useRef(window.innerWidth);

  const statusParams = (status, additionalStatus) => {
    const statusParamData = {
      status: status,
    };
    if (additionalStatus && additionalStatus.length) {
      statusParamData["additional-status"] = additionalStatus;
    }
    return statusParamData;
  };

  const handleResize = React.useCallback(() => {
    const newWidth = window.innerWidth;
    const currentWidth = screenWidthRef.current;
    screenWidthRef.current = newWidth;
    const currentIsSmall = currentWidth < 800;
    const newIsSmall = newWidth < 800;
    if (currentIsSmall !== newIsSmall) {
      setScreenWidth(newWidth);
    }
  }, []);

  const getPhysicianId = () => {
    return user?._id || null;
  };

  const initiateCommonAPIs = () => {
    socket_init();
    actions.getClientsList();
    actions.getStatesList();
    actions.getTelemedicineCategoriesList();
    actions.getArticleCategoryList();
    actions.getCredentialsList();
  };

  useEffect(() => {
    if (loggedInPhysicianDetails) {
      actions.getRxRequestsAndErrors()
      actions.getOneClickPrescribeQueue()
      const isInternalPhysician = loggedInPhysicianDetails.employmentStatus === PhysicianEmploymentStatusEnum.INTERNAL_PHYSICIAN

      const rxRenewalHandler = (data) => {
        data.physician === getPhysicianId() && actions.getRxRenewalCaseCount();
        if ([STORE_AND_FORWARD, AUDIO].includes(data.consultationType) && !data.physician) {
          actions.addCaseToQueue(loggedInPhysicianDetails, data)
        }
      };

      const newCaseHandler = (data) => {
        // Only handle regular cases, not one-click prescribe cases
        if (
          [STORE_AND_FORWARD, AUDIO].includes(data.consultationType) &&
          data.eligibleProviders.includes(loggedInPhysicianDetails.userId) &&
          !caseCapMet
        ) {
          actions.addCaseToQueue(loggedInPhysicianDetails, data)
        }
      };

      const newOneClickPrescribeCaseHandler = (data) => {
        if (data?.eligibleProviders?.includes(loggedInPhysicianDetails.userId)) {
          actions.addCaseToOneClickPrescribeQueue(data);
        }
      };

      const rxChangeHandler = (data) => {
        const { physician } = data;
        if (physician === getPhysicianId()) {
            actions.getRxChangeCaseCount();
            actions.rxChangeCaseNotification();
        }

        if ([STORE_AND_FORWARD, AUDIO].includes(data.consultationType) && !data.physician) {
          actions.addCaseToQueue(loggedInPhysicianDetails, data)
        }
      };

      const removeCaseHandler = ({ caseId }) => {
        actions.removeCaseFromQueue(caseId)
        // Mark as successful for animation, then remove after 500ms
        actions.markOneClickPrescribeCaseSuccessful(caseId)
        setTimeout(() => {
          actions.removeOneClickPrescribeCaseFromQueue(caseId)
        }, 500)
      };
      
      isInternalPhysician && socket.on("rxRenewal", rxRenewalHandler);
      isInternalPhysician && socket.on("newCase", newCaseHandler);
      isInternalPhysician && socket.on("rxChange", rxChangeHandler);
      socket.on("newOneClickPrescribeCase", newOneClickPrescribeCaseHandler);
      socket.on("caseAccepted", removeCaseHandler);
      socket.on("caseCompleted", removeCaseHandler);

      return () => {
        socket.off("rxRenewal", rxRenewalHandler);
        socket.off("newCase", newCaseHandler);
        socket.off("newOneClickPrescribeCase", newOneClickPrescribeCaseHandler);
        socket.off("rxChange", rxChangeHandler);
        socket.off("caseAccepted", removeCaseHandler);
        socket.off("caseCompleted", removeCaseHandler);
      };
    }
  }, [loggedInPhysicianDetails, caseCapMet])

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    if (user?.type === UserRoleEnum.CLINICAL_PROVIDER) {
      initiateCommonAPIs();
      actions.getCaseTicketNotificationCount();
      actions.getUnreadPatientChatsCount();
      actions.getPhysicianDetails();
      actions.getConfigs(['ONE_CLICK_PRESCRIBE_ENABLED']);
      physicianUsesReferralsFeature && actions.getReferralCases()
      actions.getCaseQueueCount(isInternalPhysician ? 'internal' : 'external')

      actions.getAdditionalInfoCaseUpdateCount(
        statusParams(
          "additional info required",
          "invalid face photo,invalid photo id"
        )
      );

      const statusInfoParams = {};
      statusInfoParams["status"] = "additional info required,invalid face photo,invalid photo id";
      actions.getAdditionalInfoCaseCount(statusInfoParams);


        socket.on("rxChange", (data) => {
            const { physician } = data;
            if (physician === getPhysicianId()) {
                actions.getRxChangeCaseCount();
                actions.rxChangeCaseNotification();
            }
        });

      socket.on("additionalInfoRequired", (data) => {
        const { physician } = data;
        if (physician === getPhysicianId()) {
          actions.getAdditionalInfoCaseUpdateCount(
            statusParams(
              "additional info required",
              "invalid face photo,invalid photo id"
            )
          );
          actions.additionalInfoCaseUpdateNotification();
        }
      });

      socket.on("additionalInfoMessageReceived", (data) => {
        const { physician } = data;
        if (physician === getPhysicianId()) {
          actions.getAdditionalInfoCaseCount(statusInfoParams);
        }
      });

      socket.on("newPatientChatMessageRecieved", (data) => {
        const { physician, caseId } = data;
        if (physician === getPhysicianId()) {
          actions.getUnreadPatientChatsCount();
          caseId && actions.getChatWithUnreadPatientMessagesCount(caseId);
        }
      });

      socket.on("markedChatAsRead", (data) => {
        const { physicianId, caseId } = data;
        if (physicianId === getPhysicianId()) {
          actions.getUnreadPatientChatsCount();
          caseId && actions.getChatWithUnreadPatientMessagesCount(caseId);
        }
      });

      socket.on("newTicketChatMessageRecieved", (data) => {
        console.log("========= HomePage newTicketChatMessageRecieved event data ==========", data);
        const { users, ticketId } = data;
        if (users?.includes(getPhysicianId())) {
          actions.getCaseTicketNotificationCount();
          actions.getUnreadMessagesCountByTicketIds([ticketId]);
        }
      });

      socket.on("markedTicketAsRead", (data) => {
        if (getPhysicianId() === data.userId) {
          actions.getUnreadMessagesCountByTicketIds([data.ticketId]);
        }
      });

      //TODO: clearTicketNotificationSend is deprecated
      socket.on("clearTicketNotificationSend", (data) => {
        const { userId } = data
        if (userId && getPhysicianId() === userId) {
          actions.getCaseTicketNotificationCount();
        }
      });

      socket.on("syncConsultAssigned", (data) => {
        const { physician } = data;
        if (physician === getPhysicianId()) {
          actions.audioVideoCaseNotification();
        }
      });
      socket.on("asyncConsultAssigned", (data) => {
        const { physician } = data;
        if (physician === getPhysicianId()) {
          actions.pendingPrescriptionCaseNotification();
        }
      });

      socket.on("consultUnassigned", (data) => {
        const { physician } = data;
        if (physician === getPhysicianId()) {
          actions.unassignedCaseNotification();
        }
      });

      socket.on("reviewCasesUpdated", () => {
        physicianUsesReferralsFeature && actions.getReferralCases()
      });

      socket.on("consultationReminder", (data) => {
        const { assignedPhysician, consultationType, caseId, startTime } = data

        if (assignedPhysician === getPhysicianId()) {
          const minutesUntilConsult = dayjs(startTime).diff(new Date(), 'minutes')

          notification.open({
            message: 'Upcoming Consultation Reminder',
            description: `You have ${consultationType === 'Audio' ? 'an' : 'a'} ${consultationType.toLowerCase()} consultation starting in ${minutesUntilConsult} minutes. Click to view case.`,
            duration: 10,
            onClick: () => history.push(`/dashboard/physician/caseview/${caseId}`)
          })
        }
      });

      socket.on('rxRequest', (message) => {
        const { type, providerId } = message
        if (providerId === getPhysicianId()) {
          actions.rxRequestNotification({ type })
        }
      })

      socket.on('rxError', (message) => {
        const { type, providerId } = message
        if (providerId === getPhysicianId()) {
          actions.rxRequestNotification({ type, providerId })
        }
      })

      return () => {
        socket.removeAllListeners([
          "refreshCases",
          "rxChange",
          "additionalInfoRequired",
          "newPatientChatMessageRecieved",
          "newTicketChatMessageRecieved",
          "clearTicketNotificationSend",
          "syncConsultAssigned",
          "asyncConsultAssigned",
          "consultUnassigned",
        ]);
        socket_close();
        window.removeEventListener("resize", handleResize);
      };
    }
  }, []);

  useReportsWithSockets()

  let HeaderProps = {
    screenWidth: screenWidth,
    userName: user?.email,
    idVerified: loggedInPhysicianDetails?.idVerified,
    pinSet: loggedInPhysicianDetails?.pinSet,
    tfaActivated: loggedInPhysicianDetails?.tfaActivated,
    setShowIdentityVerificationFlow
  };

  const handleFinishIPDVerification = () => {
    actions.getPhysicianDetails()
    setShowIdentityVerificationFlow(false)
  }

  if (!accessToken || user?.type !== UserRoleEnum.CLINICAL_PROVIDER) return <Redirect to="/login" />;

  return (
    <SocketContextProvider value={socket}>
      <TablePreferencesProvider>
        <Layout className="outer-layout">
          <HeaderPage
            {...HeaderProps}
            provider={loggedInPhysicianDetails}
            beginIdentityVerification={() => setShowIdentityVerificationFlow(true)}
          />
          <Layout style={{ display: 'flex'}}>
            <Sidebar/>
            {
              config.allowIdVerification === 'true' &&
              !!loggedInPhysicianDetails &&
              PROVIDER_HANDLES_TESTO_CASES &&
              (!loggedInPhysicianDetails.idVerified || !loggedInPhysicianDetails.pinSet) && (
              <VerifyIdentity
                idVerified={loggedInPhysicianDetails?.idVerified}
                pinSet={loggedInPhysicianDetails?.pinSet}
                open={showIdentityVerificationFlow}
                setShowIdentityVerificationFlow={setShowIdentityVerificationFlow}
                onFinish={handleFinishIPDVerification}
              />
            )}
            <Layout>
            {
              !!loggedInPhysicianDetails && (
                <Content className="inner-content" style={{
                  width: isMenuOpen ? 'calc(100% - 280px)' : 'calc(100% - 70px)',
                  marginLeft: isMenuOpen ? '280px' : '70px',
                  paddingTop: '20px'
                }}>
                  <AppRouter
                    loggedInPhysicianDetails={loggedInPhysicianDetails}
                    isSidebarCollapsed={!isMenuOpen}
                  />
                </Content>
              )
            }
            </Layout>
          </Layout>
        </Layout>
      </TablePreferencesProvider>
    </SocketContextProvider>
  );
};

const mapStateToProps = (state) => {
  const { physicianDetails } = state.physicianDetails;
  const { oneClickPrescribeQueue } = state.oneClickPrescribeQueue;

  return {
    loggedInPhysicianDetails: physicianDetails,
    oneClickPrescribeQueue: oneClickPrescribeQueue,
  };
};

const mapDispatchToProps = (dispatch) => {
  const { getUnreadPatientChatsCount, getChatWithUnreadPatientMessagesCount } = messageStore.actions;
  const { getRxRequestsAndErrors } = rxRequests.actions
  const {
    pendingPrescriptionCaseNotification,
    audioVideoCaseNotification,
    additionalInfoCaseUpdateNotification,
    rxChangeCaseNotification,
    unassignedCaseNotification,
  } = notifications.actions;
  const { getAdditionalInfoCaseUpdateCount, getAdditionalInfoCaseCount } =
    additionalInfoRequired.actions;
  const { getPhysicianDetails } = physicianDetails.actions;
  const {
    getClientsList,
    getStatesList,
    getTelemedicineCategoriesList,
    getCredentialsList,
    getArticleCategoryList,
  } = entities.actions;
  const { getRxChangeCaseCount } = rxChange.actions;
  const { getRxRenewalCaseCount } = rxRenewal.actions;
  const { getCaseTicketNotificationCount, getUnreadMessagesCountByTicketIds } = caseTickets.actions;
  const { getReferralCases } = referrals.actions
  const { getCaseQueueCount, addCaseToQueue, removeCaseFromQueue } = caseQueue.actions;
  const { getOneClickPrescribeQueue, addCaseToOneClickPrescribeQueue, removeOneClickPrescribeCaseFromQueue, markOneClickPrescribeCaseSuccessful } = oneClickPrescribeQueue.actions;
  const { rxRequestNotification } = notifications.actions
  const { getConfigs } = configs.actions;

  return {
    actions: bindActionCreators(
      {
        getUnreadPatientChatsCount,
        getChatWithUnreadPatientMessagesCount,
        pendingPrescriptionCaseNotification,
        audioVideoCaseNotification,
        additionalInfoCaseUpdateNotification,
        rxChangeCaseNotification,
        getPhysicianDetails,
        unassignedCaseNotification,
        getClientsList,
        getStatesList,
        getTelemedicineCategoriesList,
        getCredentialsList,
        getAdditionalInfoCaseUpdateCount,
        getRxChangeCaseCount,
        getRxRenewalCaseCount,
        getArticleCategoryList,
        getCaseTicketNotificationCount,
        getUnreadMessagesCountByTicketIds,
        getAdditionalInfoCaseCount,
        getReferralCases,
        getCaseQueueCount,
        addCaseToQueue,
        removeCaseFromQueue,
        addCaseToOneClickPrescribeQueue,
        removeOneClickPrescribeCaseFromQueue,
        markOneClickPrescribeCaseSuccessful,
        getOneClickPrescribeQueue,
        rxRequestNotification,
        getRxRequestsAndErrors,
        getConfigs,
      },
      dispatch
    ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(HomePage);
